import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './locales/en.json';
import pt from './locales/pt.json';
import es from './locales/es.json';
import zh from './locales/zh.json';

/**
 * Translation layer for the DAPP.
 *
 * Deliberately dependency-free: the bundle is already heavy, and the four
 * languages here mirror the landing page, which does the same job with plain
 * data attributes. The API is shaped like i18next's (`t('a.b', { count })`) so
 * swapping in the real thing later is a find-and-replace rather than a rewrite.
 */

export const LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'pt', label: 'PT', name: 'Português' },
  { code: 'es', label: 'ES', name: 'Español' },
  { code: 'zh', label: 'ZH', name: '中文' },
];

const BUNDLES = { en, pt, es, zh };
const STORAGE_KEY = 'lb-lang';
const FALLBACK = 'en';

const isSupported = (code) => LANGUAGES.some((item) => item.code === code);

/** Walks a dotted key through a bundle. */
const lookup = (bundle, key) => {
  const value = key.split('.').reduce((node, part) => {
    if (node && typeof node === 'object' && part in node) return node[part];
    return undefined;
  }, bundle);
  return typeof value === 'string' ? value : undefined;
};

/** Replaces {{name}} placeholders. Missing values are left visible, not blanked. */
const interpolate = (template, vars) => {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
  );
};

const detect = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch (err) {
    /* private mode — the choice just will not persist */
  }

  const navigatorLang = (window.navigator.language || FALLBACK).slice(0, 2).toLowerCase();
  return isSupported(navigatorLang) ? navigatorLang : FALLBACK;
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detect);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!isSupported(next)) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key, vars) => {
      const hit = lookup(BUNDLES[lang], key) ?? lookup(BUNDLES[FALLBACK], key);

      if (hit === undefined) {
        // Showing the key beats showing nothing: a blank label is invisible in
        // review, a stray "round.closesIn" is not.
        if (process.env.NODE_ENV === 'development') {
          console.warn('[i18n] missing key:', key);
        }
        return key;
      }

      return interpolate(hit, vars);
    },
    [lang]
  );

  const value = useMemo(() => ({ t, lang, setLang }), [t, lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used inside <I18nProvider>');
  }
  return ctx;
};

/** Shorthand for components that only need the translator. */
export const useT = () => useI18n().t;

export default I18nContext;
